import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import type { BesprechungEinwand, SiteDocDetail } from '@symphenia/shared-types'
import { Feld as Spalte, ZeilenBlock, ZeilenListe, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { datumOhneVorlage, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fehlertext } from '@/lib/fehlertext'
import { besprechungKopf } from './api'

const FELDER = ['von', 'eingangAm', 'punkt', 'einwand', 'berichtigung'] as const
const heute = () => new Date().toISOString().slice(0, 10)

/**
 * AP-49 7.2 — **Einwände der Berichtigung**: je Einwand von (Name, Firma), Eingang am, betroffener Punkt,
 * Einwand und — optional — die Berichtigung. Die Liste steht im Kopf der Berichtigung und wird als Ganzes
 * geschrieben (eine Speicherstelle, AP-36 8.3 a); jede Zeile ist eine Gruppe. Das Protokoll selbst bleibt
 * eingefroren (BS-11).
 */
export function EinwaendeBlock({ dok, einwaende, onGeaendert }: { dok: SiteDocDetail; einwaende: BesprechungEinwand[]; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const [neu, setNeu] = useState({ von: '', einwand: '' })
  const daten = useMemo<Datensatz>(() => Object.fromEntries(einwaende.flatMap((e) => FELDER.map((f) => [`${e.key}|${f}`, e[f] ?? null]))), [einwaende])
  const schreibeListe = async (liste: BesprechungEinwand[]) => {
    const r = await besprechungKopf(dok.id, { berichtigungen: liste })
    onGeaendert(r)
    return r
  }
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungEinwaende:${dok.id}`,
    bezeichnung: t('siteDocs.meeting.objections'),
    daten,
    schreibe: async (patch) => {
      const liste = einwaende.map((e) => {
        const n = { ...e }
        for (const f of FELDER) {
          const k = `${e.key}|${f}`
          if (k in patch) (n as Record<string, unknown>)[f] = patch[k] ?? (f === 'von' || f === 'einwand' || f === 'eingangAm' ? '' : null)
        }
        return n
      })
      const r = await schreibeListe(liste)
      const neu = ((r.content?.berichtigungen ?? []) as BesprechungEinwand[])
      return Object.fromEntries(neu.flatMap((e) => FELDER.map((f) => [`${e.key}|${f}`, e[f] ?? null])))
    },
  }) as Speicherstelle<unknown>
  const spalten: SpaltenDef[] = [
    { key: 'von', label: t('siteDocs.meeting.objectionFrom'), spur: 'minmax(10rem,1.5fr)', min: 160 },
    { key: 'eingangAm', label: t('siteDocs.meeting.objectionReceived'), spur: '9rem', min: 144 },
    { key: 'punkt', label: t('siteDocs.meeting.objectionItem'), spur: '4.5rem', min: 72 },
    { key: 'einwand', label: t('siteDocs.meeting.objection'), spur: 'minmax(12rem,2fr)', min: 192 },
    { key: 'berichtigung', label: t('siteDocs.meeting.correction'), spur: 'minmax(12rem,2fr)', min: 192 },
    { key: 'aktion', label: '', spur: '2rem', min: 32 },
  ]
  const anlegen = async () => {
    if (!neu.von.trim() && !neu.einwand.trim()) return
    setFehler(null)
    try {
      await schreibeListe([...einwaende, { key: `ew-${crypto.randomUUID()}`, von: neu.von.trim(), eingangAm: heute(), punkt: null, einwand: neu.einwand.trim(), berichtigung: null }])
      setNeu({ von: '', einwand: '' })
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium">{t('siteDocs.meeting.objections')}</h3>
      <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.objectionsHint')}</p>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
      <ZeilenListe spalten={spalten}>
        {einwaende.map((e) => (
          <EinwandZeile
            key={e.key}
            stelle={stelle}
            einwand={e}
            onLoeschen={() => {
              setFehler(null)
              schreibeListe(einwaende.filter((x) => x.key !== e.key)).catch((err) => setFehler(fehlertext(err, t)))
            }}
          />
        ))}
        <ZeilenBlock neu autoFokus={false} onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu({ von: '', einwand: '' })}>
          <Spalte spalte="von">
            <input className={zelle} placeholder={t('siteDocs.meeting.newObjection')} aria-label={t('siteDocs.meeting.objectionFrom')} value={neu.von} onChange={(x) => setNeu({ ...neu, von: x.target.value })} />
          </Spalte>
          <Spalte spalte="eingangAm" leer>
            <span />
          </Spalte>
          <Spalte spalte="punkt" leer>
            <span />
          </Spalte>
          <Spalte spalte="einwand">
            <input className={zelle} aria-label={t('siteDocs.meeting.objection')} value={neu.einwand} onChange={(x) => setNeu({ ...neu, einwand: x.target.value })} />
          </Spalte>
          <Spalte spalte="berichtigung" leer>
            <span />
          </Spalte>
          <Spalte spalte="aktion" leer>
            <span />
          </Spalte>
        </ZeilenBlock>
      </ZeilenListe>
    </section>
  )
}

function EinwandZeile({ stelle, einwand, onLoeschen }: { stelle: Speicherstelle<unknown>; einwand: BesprechungEinwand; onLoeschen: () => void }) {
  const { t } = useTranslation()
  const gruppe = useGruppe(stelle, { label: einwand.von })
  const von = useFeld(stelle, `${einwand.key}|von`, { gruppe, pflicht: true, label: t('siteDocs.meeting.objectionFrom') })
  const am = useFeld(stelle, `${einwand.key}|eingangAm`, { gruppe, art: 'datum', pflicht: true, label: t('siteDocs.meeting.objectionReceived') })
  const punkt = useFeld(stelle, `${einwand.key}|punkt`, { gruppe, label: t('siteDocs.meeting.objectionItem') })
  const text = useFeld(stelle, `${einwand.key}|einwand`, { gruppe, pflicht: true, label: t('siteDocs.meeting.objection') })
  const berichtigung = useFeld(stelle, `${einwand.key}|berichtigung`, { gruppe, label: t('siteDocs.meeting.correction') })
  return (
    <ZeilenBlock gruppe={gruppe} schluessel={einwand.key}>
      <Spalte spalte="von">
        <input className={zelle} data-start aria-label={t('siteDocs.meeting.objectionFrom')} {...von.props} />
      </Spalte>
      <Spalte spalte="eingangAm">
        <input type="date" className={`${zelle} ${datumOhneVorlage(am.props.value)}`} aria-label={t('siteDocs.meeting.objectionReceived')} {...am.props} />
      </Spalte>
      <Spalte spalte="punkt">
        <input className={zelle} aria-label={t('siteDocs.meeting.objectionItem')} {...punkt.props} />
      </Spalte>
      <Spalte spalte="einwand">
        <input className={zelle} aria-label={t('siteDocs.meeting.objection')} {...text.props} />
      </Spalte>
      <Spalte spalte="berichtigung">
        <input className={zelle} aria-label={t('siteDocs.meeting.correction')} {...berichtigung.props} />
      </Spalte>
      <Spalte spalte="aktion">
        <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={t('siteDocs.meeting.removeObjection')} title={t('siteDocs.meeting.removeObjection')} onClick={onLoeschen}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}
